import * as React from 'react';
import findIndex from 'lodash/findIndex';
import without from 'lodash/without';
import uniqueId from 'lodash/uniqueId';

import {classify} from 'src/utils';

import type {
  MultipleChoiceOption,
  MultipleChoiceQuestion,
} from 'src/types/survey';
import {multipleChoiceOptionMaxLength} from 'src/components/workflow/event/content/module/editors/constants';

import {
  WritebackContext,
  UPDATE_QUESTION,
} from 'src/components/writeback/writeback-wrapper';
import Input from 'src/components/lib/error-input';
import Toggle from 'src/components/lib/toggle/toggle';
import Checkbox from 'src/components/lib/checkbox';

import DeleteIcon from 'src/images/delete-icon.svg';
import ExpandIcon from 'src/images/expand-icon.svg';

import moduleCss from 'src/components/workflow/event/content/module/editors/module.css';


export default ({
  conditionalAlerts,
  error,
}: {
  conditionalAlerts?: boolean;
  error?: {
    question?: {
      errors: string[];
    };
    choices?: {
      [key: string]: {
        value: {
          errors: string[];
        };
      };
    };
  };
}): React.ReactElement<React.ComponentProps<'div'>> => {
  const {state, dispatch} = React.useContext(WritebackContext);
  const currentQuestionType = state.question?.selectedType;
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'QuestionType' can't be used to index type 'CurrentQuestion'.
  const currentQuestion = state.question?.[currentQuestionType];

  const writebackEnabled = currentQuestion.writeback?.enabled;

  const writebackField = currentQuestion.writeback?.attribute_name;
  let fieldPossibleValues: Array<string> = [];
  if (
    writebackField &&
    state.attribute_meta_data?.[writebackField]?.possible_values
  ) {
    fieldPossibleValues =
      state.attribute_meta_data?.[writebackField]?.possible_values;
  }

  const allowAddingNewValues =
    writebackField &&
    state.attribute_meta_data?.[writebackField]?.allow_adding_new_values;

  const choices: Array<{
    id: string;
    isNew?: boolean;
    value: string;
  }> = currentQuestion?.choices.map((choice: MultipleChoiceOption) => {
    return fieldPossibleValues?.includes(choice.value)
      ? choice
      : {...choice, isNew: true};
  });

  const getInitialCustomMultipleChoiceOptions = fieldPossibleValues
    ? choices
        .filter((choice) => !fieldPossibleValues.includes(choice.value))
        .map((choice) => ({...choice, isNew: true}))
    : [];

  const [showAllWritebackChoices, setShowAllWritebackChoices] = React.useState(
    !!getInitialCustomMultipleChoiceOptions.length,
  );

  const customMultipleChoiceOptions =
    currentQuestion?.customMultipleChoiceOptions ||
    getInitialCustomMultipleChoiceOptions;

  // @ts-expect-error - TS7006 - Parameter 'attrs' implicitly has an 'any' type.
  const updateQuestion = (attrs) => {
    dispatch({
      type: UPDATE_QUESTION,
      payload: {
        questionType: currentQuestionType,
        attrs,
      },
    });
  };

  const addCustomMultipleChoiceOptionsHandler = () => {
    updateQuestion({
      customMultipleChoiceOptions: [
        ...customMultipleChoiceOptions,
        {value: '', id: uniqueId('choice_'), isNew: true},
      ],
    });
  };

  const removeCustomMultipleChoiceOptionsHandler = (choiceId: string) => {
    const updatedChoices = choices.filter((choice) => choice.id !== choiceId);
    const updatedCustomMultipleChoiceOptions =
      // @ts-expect-error - TS7006 - Parameter 'choice' implicitly has an 'any' type.
      customMultipleChoiceOptions.filter((choice) => choice.id !== choiceId);
    updateQuestion({
      choices: updatedChoices,
      customMultipleChoiceOptions: updatedCustomMultipleChoiceOptions,
    });
  };

  React.useEffect(() => {
    if (choices.length === 0) {
      updateQuestion({
        choices: [
          {value: '', id: uniqueId('choice_')},
          {value: '', id: uniqueId('choice_')},
        ],
      });
    }
  }, []);

  const choicesText = currentQuestion.writeback?.choices_text ?? {};

  const choiceValues = choices.map((choice) => choice.value);

  const choiceIds = choices.map((choice) => choice.id);

  const updateNewChoiceValue = (
    id: string,
    newValue: string,
    oldValue: string,
  ) => {
    const foundIndex = customMultipleChoiceOptions.findIndex(
      // @ts-expect-error - TS7006 - Parameter 'x' implicitly has an 'any' type.
      (x) => x.id === id,
    );
    const updatedNewChoice = {
      ...customMultipleChoiceOptions[foundIndex],
      value: newValue,
    } as const;
    const updatedNewChoices = [...customMultipleChoiceOptions];
    updatedNewChoices[foundIndex] = updatedNewChoice;

    const updatedChoices = [...choices];

    if (choiceValues.includes(oldValue)) {
      const foundIndex = choices.findIndex(
        (x) => x.id === id && x.isNew === true,
      );
      const updatedNewChoice = {
        ...choices[foundIndex],
        value: newValue,
      } as const;
      updatedChoices[foundIndex] = updatedNewChoice;
    }

    const updatedChoicesText = {...choicesText} as const;

    updateQuestion({
      choices: updatedChoices,
      writeback: {
        ...currentQuestion.writeback,
        choices_text: updatedChoicesText,
      },
      customMultipleChoiceOptions: updatedNewChoices,
    });
  };

  const newToggleClickHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
    isNew: boolean,
    value: string,
    id: string,
    index: number,
  ) => {
    if (event.currentTarget.checked === true) {
      if (
        !value ||
        fieldPossibleValues.includes(value) ||
        choiceValues.includes(value)
      ) {
        // show error
        return;
      }
      const {writeback = {}} = currentQuestion || {};
      const choices_text = {
        ...writeback.choices_text,
        [value]: value,
      } as const;
      updateQuestion({
        choices: [...choices, {value, id, isNew}],
        writeback: {
          ...writeback,
          choices_text,
        },
      });
    } else {
      let updatedChoicesText = {...choicesText};
      if (value in choicesText) {
        const {[value]: _, ...restChoicesText} = choicesText;
        updatedChoicesText = {
          ...restChoicesText,
        };
        updateQuestion({
          choices: choices.filter((item) => item.id !== id),
          writeback: {
            ...currentQuestion.writeback,
            choices_text: updatedChoicesText,
          },
        });
      }
    }
  };

  const sortedPossibleValues = React.useMemo(
    () => {
      const disabledChoices = fieldPossibleValues
        ? fieldPossibleValues
            .filter((choice) => !choiceValues.includes(choice))
            .map((choice) => ({
              value: choice,
              id: uniqueId('choice_'),
            }))
        : [];

      const disabledNewChoices = customMultipleChoiceOptions.filter(
        // @ts-expect-error - TS7006 - Parameter 'choice' implicitly has an 'any' type.
        (choice) => !choiceIds.includes(choice.id),
      );

      return [...choices, ...disabledChoices, ...disabledNewChoices];
    },
    [writebackField, customMultipleChoiceOptions], //not including choices as a dependency here because we don't want to re-sort when choices changes
  );

  let multipleChoiceOptions;
  if (currentQuestion.writeback?.enabled) {
    // Note: when writeback enabled - this is to handle "show 4 options" and "expand".
    multipleChoiceOptions = showAllWritebackChoices
      ? sortedPossibleValues
      : sortedPossibleValues.slice(0, 4);
  } else {
    multipleChoiceOptions = choices;
  }

  const showChoices = currentQuestion.writeback?.enabled
    ? Boolean(writebackField)
    : true;

  const rowStyles = classify(moduleCss.multipleChoiceModule_choicesContainer, {
    [moduleCss.withWriteback]: currentQuestion.writeback?.enabled,
    [moduleCss.withAlerts]: conditionalAlerts,
    [moduleCss.withNewWriteback]:
      currentQuestion?.writeback?.enabled && customMultipleChoiceOptions.length,
  });

  return (
    <div className={moduleCss.multipleChoiceModule_container}>
      {showChoices && (
        <div className={moduleCss.multipleChoiceModule_rows}>
          <div className={rowStyles}>
            {writebackEnabled && (
              <div className={moduleCss.multipleChoiceModule_choiceColumnTitle}>
                ATS value saved
              </div>
            )}
            {writebackEnabled && (
              <div className={moduleCss.multipleChoiceModule_choiceColumnTitle}>
                Enabled
              </div>
            )}
            <div className={moduleCss.multipleChoiceModule_choiceColumnTitle}>
              Option text recipient sees
            </div>
            {conditionalAlerts && (
              <div
                className={classify(
                  moduleCss.multipleChoiceModule_choiceColumnTitle,
                  moduleCss.multipleChoiceModule_alertItem,
                )}
              >
                Alert
              </div>
            )}
          </div>

          {multipleChoiceOptions.map(({value, id, isNew = false}, index) => (
            <div key={id} className={rowStyles}>
              {/* @ts-expect-error - TS2786 - 'Input' cannot be used as a JSX component. */}
              <Input
                // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
                type="text"
                // @ts-expect-error - TS2322 - Type 'any' is not assignable to type 'never'.
                disabled={
                  currentQuestion.writeback?.enabled &&
                  (!isNew || choiceIds.includes(id))
                }
                // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
                className={moduleCss.multipleChoiceModule_choiceInput}
                // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
                placeholder="Please enter an option"
                // @ts-expect-error - TS2322 - Type 'any' is not assignable to type 'never'.
                value={value}
                // @ts-expect-error - TS2322 - Type '(event: any) => void' is not assignable to type 'never'. | TS7006 - Parameter 'event' implicitly has an 'any' type.
                onChange={(event) => {
                  if (isNew) {
                    updateNewChoiceValue(id, event.target.value, value);
                  } else {
                    const updatedChoices = choices.slice();
                    //non writeback choice index is always consistent
                    updatedChoices[index] = {
                      value: event.target.value,
                      id: updatedChoices[index].id,
                    };
                    updateQuestion({choices: updatedChoices});
                  }
                }}
                // @ts-expect-error - TS2322 - Type 'string[]' is not assignable to type 'never'.
                errors={
                  (!currentQuestion.writeback?.enabled &&
                    error?.choices?.[id]?.value?.errors) ||
                  (isNew && fieldPossibleValues?.includes(value)
                    ? ['This value already exist']
                    : [])
                }
                data-qa-id={`option-${index}-input`}
              />

              {currentQuestion.writeback?.enabled && (
                <Toggle
                  checked={choiceIds.includes(id)}
                  className={moduleCss.multipleChoiceModule_choiceInput}
                  onChange={(event) => {
                    if (isNew) {
                      newToggleClickHandler(event, isNew, value, id, index);
                    } else {
                      const {writeback} = currentQuestion;
                      const choices_text = writeback.choices_text[value]
                        ? writeback.choices_text
                        : {
                            ...writeback.choices_text,
                            [value]: value,
                          };
                      // if below if condition is true mean toggle was true and it has been made false.
                      if (choiceIds.includes(id)) {
                        updateQuestion({
                          choices: choices.filter((item) => item.id !== id),
                        });
                      } else {
                        updateQuestion({
                          choices: [...choices, {value, id, isNew}],
                          writeback: {
                            ...writeback,
                            choices_text,
                          },
                        });
                      }
                    }
                  }}
                  qaId={`${value}-option`}
                />
              )}

              {currentQuestion.writeback?.enabled && (
                // @ts-expect-error - TS2786 - 'Input' cannot be used as a JSX component.
                <Input
                  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
                  type="text"
                  // @ts-expect-error - TS2322 - Type 'boolean' is not assignable to type 'never'.
                  disabled={!choiceIds.includes(id)}
                  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
                  className={moduleCss.multipleChoiceModule_choiceInput}
                  // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'never'.
                  placeholder="Please enter option text"
                  // @ts-expect-error - TS2322 - Type 'any' is not assignable to type 'never'.
                  value={choicesText[value] || ''}
                  // @ts-expect-error - TS2322 - Type '(event: any) => void' is not assignable to type 'never'. | TS7006 - Parameter 'event' implicitly has an 'any' type.
                  onChange={(event) => {
                    const updatedChoicesText = {
                      ...choicesText,
                      [value]: event.target.value,
                    } as const;
                    updateQuestion({
                      writeback: {
                        ...currentQuestion.writeback,
                        choices_text: updatedChoicesText,
                      },
                    });
                  }}
                  // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'never'.
                  maxLength={multipleChoiceOptionMaxLength}
                  // @ts-expect-error - TS2322 - Type 'string[] | undefined' is not assignable to type 'never'.
                  errors={error?.choices?.[id]?.value?.errors}
                />
              )}

              {conditionalAlerts && (
                <label
                  className={classify(
                    moduleCss.multipleChoiceModule_choiceInput,
                    moduleCss.multipleChoiceModule_alertItem,
                  )}
                >
                  <Checkbox
                    checked={currentQuestion.alertChoices.includes(id)}
                    disabled={!choiceValues.includes(value)}
                    onChange={(event) => {
                      let alertChoices;
                      if (currentQuestion.alertChoices?.includes(id)) {
                        alertChoices = without(
                          currentQuestion.alertChoices,
                          id,
                        );
                      } else {
                        alertChoices = [...currentQuestion.alertChoices, id];
                      }
                      updateQuestion({alertChoices});
                    }}
                  />
                </label>
              )}

              {choices.length > 1 &&
                (!currentQuestion?.writeback?.enabled ||
                  (currentQuestion.writeback?.enabled && isNew)) && (
                  <button
                    className={classify(
                      moduleCss.multipleChoiceModule_choiceInput,
                      moduleCss.multipleChoiceModule_deleteIcon,
                    )}
                    onClick={() => {
                      const updatedChoices = choices.slice(); // coping the aaray
                      updatedChoices.splice(index, 1);
                      updateQuestion({choices: updatedChoices});
                      isNew && removeCustomMultipleChoiceOptionsHandler(id);
                    }}
                  >
                    <DeleteIcon />
                  </button>
                )}
            </div>
          ))}
        </div>
      )}
      {!currentQuestion.writeback?.enabled && (
        <div
          className={moduleCss.multipleChoiceModule_addRow}
          onClick={() => {
            // setShowDefaultRow(false);
            if (!choices.length) {
              updateQuestion({
                choices: [
                  {value: '', id: uniqueId('choice_')},
                  {value: '', id: uniqueId('choice_')},
                ],
              });
            } else {
              updateQuestion({
                choices: [...choices, {value: '', id: uniqueId('choice_')}],
              });
            }
          }}
        >
          Add another option
        </div>
      )}
      {currentQuestion.writeback?.enabled &&
        allowAddingNewValues &&
        (showAllWritebackChoices || fieldPossibleValues.length <= 4) && (
          <div
            className={moduleCss.multipleChoiceModule_addRow}
            onClick={addCustomMultipleChoiceOptionsHandler}
          >
            Add another option
          </div>
        )}
      {currentQuestion.writeback?.enabled &&
        writebackField &&
        fieldPossibleValues && (
          <div className={moduleCss.multipleChoiceModule_choiceToggles}>
            <div>
              <a
                className={moduleCss.multipleChoiceModule_choiceEnableAllToggle}
                onClick={() => {
                  if (choices.length !== fieldPossibleValues.length) {
                    setShowAllWritebackChoices(true);
                    updateQuestion({
                      choices: sortedPossibleValues,
                      writeback: {
                        ...currentQuestion.writeback,
                        choices_text: sortedPossibleValues.reduce<
                          Record<string, any>
                        >((result, {value}) => {
                          result[value] = currentQuestion.writeback
                            .choices_text[value]
                            ? currentQuestion.writeback.choices_text[value]
                            : value;
                          return result;
                        }, {}),
                      },
                    });
                  }
                }}
                data-qa-id="enable-all-multiple-choice"
              >
                enable all
              </a>
              <div
                className={
                  moduleCss.multipleChoiceModule_choiceEnableAllToggleDivider
                }
              >
                /
              </div>
              <a
                className={moduleCss.multipleChoiceModule_choiceEnableAllToggle}
                onClick={() => {
                  setShowAllWritebackChoices(true);
                  updateQuestion({choices: []});
                }}
              >
                disable all
              </a>
            </div>
            {fieldPossibleValues.length > 4 && (
              <div
                className={moduleCss.multipleChoiceModule_choiceEnableAllToggle}
                onClick={() =>
                  setShowAllWritebackChoices(!showAllWritebackChoices)
                }
              >
                {!showAllWritebackChoices && (
                  <span
                    className={moduleCss.multipleChoiceModule_hiddenText}
                  >{`${fieldPossibleValues.length - 4} ${
                    fieldPossibleValues.length === 5 ? 'option' : 'options'
                  } hidden`}</span>
                )}
                <span>{showAllWritebackChoices ? 'Collapse' : 'Expand'}</span>
                <ExpandIcon
                  className={moduleCss.multipleChoiceModule_expandIcon}
                />
              </div>
            )}
          </div>
        )}
      <div className={moduleCss.multipleChoiceModule_hintText}>
        {conditionalAlerts
          ? 'The recipient will only see the enabled options above. Any alert emails will be sent if the recipient selects an option checked for alert above.'
          : 'The recipient will only see the enabled options above.'}
      </div>
    </div>
  );
};
